// File and link helpers that also work inside the zun OMS mobile app.
//
// In a normal browser these behave exactly like the old inline code
// (an <a download> click, window.open). Inside the Capacitor app a
// WebView cannot download blobs or open new tabs, so the file is written
// to the app cache and handed to the phone's share sheet (save to Files,
// Drive, WhatsApp...), and links open in the in-app browser.
//
// The native plugins (Filesystem, Share, Browser) live in the app shell;
// this file only talks to them through window.Capacitor, so the web build
// needs no extra dependencies.
//
// The Flutter app (zunenergy/oms-app) injects window.flutter_inappwebview and
// a window.ZunNative bridge instead. Files are handed to it as a data: URL,
// because the shell reads blob: URLs asynchronously and the old code revoked
// the blob URL straight after the click.

const plugins = () => window.Capacitor?.Plugins || {};

const isFlutterApp = () => !!window.flutter_inappwebview;

export const isNativeApp = () => !!window.Capacitor?.isNativePlatform?.() || isFlutterApp();

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const saveBlob = async (blob, filename) => {
  const { Filesystem, Share } = plugins();
  if (isNativeApp() && Filesystem && Share) {
    const safeName = String(filename).replace(/[\\/:*?"<>|]/g, '_');
    const { uri } = await Filesystem.writeFile({
      path: safeName,
      data: await blobToBase64(blob),
      directory: 'CACHE',
    });
    await Share.share({ title: safeName, files: [uri] });
    return;
  }

  if (isFlutterApp()) {
    const type = (blob.type || 'application/octet-stream').replace(/;+$/, '');
    const dataUrl = `data:${type};base64,${await blobToBase64(blob)}`;
    const payload = { url: dataUrl, fileName: String(filename) };
    if (window.ZunNative?.download) {
      await window.ZunNative.download(payload);
    } else {
      await window.flutter_inappwebview.callHandler('zunNativeDownload', payload);
    }
    return;
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const toCsvCell = (value) => {
  let str = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  return `"${str.replace(/"/g, '""')}"`;
};

export const openUrl = (url) => {
  const { Browser } = plugins();
  if (isNativeApp() && Browser) {
    Browser.open({ url });
    return;
  }
  window.open(url, '_blank');
};
